import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "dropdown", "hiddenInput"]

  connect() {
    this.recommendationSources = []
    this.loadRecommendationSources()
    this.setupEventListeners()
    
    // Verificar si ya hay un valor al conectar
    setTimeout(() => {
      this.updateFieldStatus()
    }, 100)
  }

  disconnect() {
    this.removeEventListeners()
  }

  async loadRecommendationSources() {
    try {
      const response = await fetch('/recommendation_sources.json', {
        headers: {
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        }
      })
      this.recommendationSources = await response.json()
    } catch (error) {
      console.error('Error loading recommendation sources:', error)
    }
  }

  setupEventListeners() {
    this.inputTarget.addEventListener('input', this.handleInput.bind(this))
    this.inputTarget.addEventListener('keydown', this.handleKeydown.bind(this))
    this.inputTarget.addEventListener('focus', this.handleFocus.bind(this))
    this.inputTarget.addEventListener('blur', this.hideDropdown.bind(this))
    
    // Close dropdown when clicking outside
    document.addEventListener('click', this.handleOutsideClick.bind(this))
  }

  removeEventListeners() {
    this.inputTarget.removeEventListener('input', this.handleInput.bind(this))
    document.removeEventListener('click', this.handleOutsideClick.bind(this))
  }

  handleInput(event) {
    const originalQuery = event.target.value
    const query = originalQuery.toLowerCase()
    this.filterOptions(query, originalQuery)
    this.updateFieldStatus()
  }

  handleKeydown(event) {
    if (event.key === 'Enter') {
      event.preventDefault()
      const query = this.inputTarget.value.trim()
      if (query) {
        this.createRecommendationSource(query)
      }
    }
  }

  handleOutsideClick(event) {
    if (!this.element.contains(event.target)) {
      this.hideDropdown()
    }
  }

  handleFocus(event) {
    this.showAllOptions()
  }

  filterOptions(query, originalQuery = '') {
    if (!query) {
      this.showAllOptions()
      return
    }

    const filtered = this.recommendationSources.filter(source => 
      source.name.toLowerCase().includes(query)
    )

    if (filtered.length === 0) {
      this.showCreateOption(originalQuery || query)
    } else {
      this.showFilteredOptions(filtered, originalQuery || query)
    }
  }

  showAllOptions() {
    if (this.recommendationSources.length === 0) {
      this.dropdownTarget.innerHTML = '<div class="dropdown-item text-muted">No se encontraron recomendaciones</div>'
    } else {
      this.dropdownTarget.innerHTML = this.recommendationSources
        .map(source => this.createOptionHTML(source.name, source.id, false))
        .join('')
    }
    this.showDropdown()
  }

  showFilteredOptions(filtered, query) {
    // Check if there's already a recommendation source with the same name (case-insensitive)
    const existingSource = this.recommendationSources.find(source => 
      source.name.toLowerCase() === query.toLowerCase()
    )
    
    if (existingSource) {
      // Don't show create option if it already exists
      this.dropdownTarget.innerHTML = filtered
        .map(source => this.createOptionHTML(source.name, source.id, false))
        .join('')
    } else {
      // Show create option if it doesn't exist
      this.dropdownTarget.innerHTML = filtered
        .map(source => this.createOptionHTML(source.name, source.id, false))
        .join('') + this.createOptionHTML(`Crear "${query}"`, null, true)
    }
    this.showDropdown()
  }

  showCreateOption(query) {
    // Check if there's already a recommendation source with the same name (case-insensitive)
    const existingSource = this.recommendationSources.find(source => 
      source.name.toLowerCase() === query.toLowerCase()
    )
    
    if (existingSource) {
      // If it exists, show it as a regular option instead of create option
      this.dropdownTarget.innerHTML = this.createOptionHTML(existingSource.name, existingSource.id, false)
    } else {
      // Show create option if it doesn't exist
      this.dropdownTarget.innerHTML = this.createOptionHTML(`Crear "${query}"`, null, true)
    }
    this.showDropdown()
  }

  createOptionHTML(text, value, isCreate = false) {
    const className = isCreate ? 'dropdown-item create-option' : 'dropdown-item'
    const dataValue = value ? `data-value="${value}"` : ''

    return `<div class="${className}" ${dataValue}>${text}</div>`
  }

  showDropdown() {
    this.dropdownTarget.style.display = 'block'
    this.setupDropdownClickListeners()
  }

  hideDropdown() {
    setTimeout(() => {
      this.dropdownTarget.style.display = 'none'
    }, 150)
  }

  setupDropdownClickListeners() {
    const options = this.dropdownTarget.querySelectorAll('.dropdown-item')
    options.forEach(option => {
      option.addEventListener('click', (event) => {
        event.preventDefault()
        const text = option.textContent
        const value = option.dataset.value
        
        if (option.classList.contains('create-option')) {
          // Extract the name from "Crear 'nombre'"
          const name = text.match(/Crear "(.+)"/)?.[1] || text
          this.createRecommendationSource(name)
        } else {
          this.selectOption(text, value)
        }
      })
    })
  }

  selectOption(text, value) {
    this.inputTarget.value = text
    if (this.hasHiddenInputTarget) {
      this.hiddenInputTarget.value = value
    }
    this.hideDropdown()
    this.updateFieldStatus()
  }

  updateFieldStatus() {
    const hasValue = this.inputTarget.value.trim() !== '' && this.hiddenInputTarget.value !== ''
    
    if (hasValue) {
      this.inputTarget.classList.add('is-valid')
      this.inputTarget.classList.remove('is-invalid')
      this.inputTarget.nextElementSibling.style.right = '30px'
    } else {
      this.inputTarget.classList.remove('is-valid')
      this.inputTarget.classList.remove('is-invalid')
      this.inputTarget.nextElementSibling.style.right = '10px'
    }
  }

  async createRecommendationSource(name) {
    // Check if a recommendation source with the same name (case-insensitive) already exists
    const existingSource = this.recommendationSources.find(source => 
      source.name.toLowerCase() === name.toLowerCase()
    )
    
    if (existingSource) {
      // Select the existing option instead
      this.selectOption(existingSource.name, existingSource.id)
      return
    }
    
    try {
      const response = await fetch('/recommendation_sources', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({
          recommendation_source: { name: name }
        })
      })

      const data = await response.json()
      
      if (data.success) {
        // Add to local list
        this.recommendationSources.push(data.recommendation_source)
        
        // Select the new option
        this.selectOption(data.recommendation_source.name, data.recommendation_source.id)
        
        // Show success message using Rails flash
        this.showFlash('Fuente de recomendación creada exitosamente', 'success')
      } else {
        this.showFlash('Error al crear la fuente de recomendación: ' + data.errors.join(', '), 'error')
      }
    } catch (error) {
      console.error('Error creating recommendation source:', error)
      this.showFlash('Error al crear la fuente de recomendación', 'error')
    }
  }

  showFlash(message, type) {
    // Crear un elemento flash que se integre con el sistema de Rails
    const flashContainer = document.querySelector('#flashes') || document.body
    
    const flashElement = document.createElement('div')
    flashElement.className = `flash flash-${type === 'success' ? 'notice' : 'alert'} alert alert-dismissible fade show`
    flashElement.setAttribute('role', 'alert')
    flashElement.setAttribute('style', 'z-index: 9999;')
    flashElement.setAttribute('data-flash-key', type === 'success' ? 'notice' : 'alert')
    flashElement.setAttribute('data-controller', 'flash')
    flashElement.setAttribute('data-flash-auto-dismiss-value', 'true')
    flashElement.setAttribute('data-flash-dismiss-delay-value', '2000')
    
    flashElement.innerHTML = `
      <span class="w-100 text-center">${message}</span>
      <a data-dismiss="alert" aria-label="Close" class="text-secondary">
        <i class="fas fa-times"></i>
      </a>
    `
    
    flashContainer.appendChild(flashElement)
    
    // Auto remove after 2 seconds (same as Rails flashes)
    setTimeout(() => {
      if (flashElement.parentNode) {
        flashElement.remove()
      }
    }, 2000)
  }
} 